/**
 * Cross-subdomain "is the user authed?" flag.
 *
 * Why this exists:
 * - The session is stored in localStorage, which is *origin*-scoped. That
 *   means a session created on `app.unfoldwin.com` is invisible to
 *   `unfoldwin.com` (and vice versa) — so the marketing site can't tell who
 *   is logged in.
 * - Rather than try to ship the entire session JSON across origins (cookies
 *   are size-limited and security-sensitive), we drop a tiny cookie scoped
 *   to the *parent* domain `.unfoldwin.com`. Both subdomains see this cookie
 *   and we use it purely as a signal: "if you see this, redirect the user
 *   over to the app where the real session lives".
 *
 * No PII, no tokens — just a "1" string and an expiry.
 */
import { isCrossDomainConfigured } from "@/lib/auth-redirect";

const COOKIE_NAME = "unfold_authed";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days, mirrors the backend's refresh token TTL

function getCookieDomain(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  // Only use a parent-domain cookie when we're actually on a *.unfoldwin.com
  // host. In dev (localhost / vercel preview) we skip the flag entirely;
  // there's only one origin so it isn't needed.
  if (host === "unfoldwin.com" || host.endsWith(".unfoldwin.com")) {
    return ".unfoldwin.com";
  }
  return null;
}

function isHttps(): boolean {
  return typeof window !== "undefined" && window.location.protocol === "https:";
}

/** Best-effort: only operates in real cross-domain prod. No-op everywhere else. */
export function setAuthFlag(): void {
  if (typeof document === "undefined") return;
  if (!isCrossDomainConfigured()) return;
  const domain = getCookieDomain();
  if (!domain) return;
  const secure = isHttps() ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=1; Domain=${domain}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

export function clearAuthFlag(): void {
  if (typeof document === "undefined") return;
  const domain = getCookieDomain();
  if (!domain) return;
  // Expire immediately. Set on parent domain so both subdomains drop it.
  document.cookie = `${COOKIE_NAME}=; Domain=${domain}; Path=/; Max-Age=0; SameSite=Lax`;
}

export function hasAuthFlag(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${COOKIE_NAME}=1`));
}
